// QC Hold, phase 1 (rule 30, migration 031): the Holds panel on Goods in, and "Put batch on QC Hold"
// on an inspection failure and on a certificate batch card.
//
// The app writes NOTHING to D365. It stores the batch's disposition code before the hold, tells the
// person exactly what to set in D365's Batches form, and reads D365 back (the timer, or Check now).
// Release puts back the code the batch had - never a constant.
//
// The buttons hide while QUALITY_BATCH_HOLD is off, and while the switch could not be read; the
// panel says which of the three it is (UI/UX audit, 31 Oct 2026, UX-10).
// Rule 15: "D365 now" that could not be read says "Not known", never "not on hold".
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Saved } from '@/components/Saved';
import { api, ApiError } from '../../lib/api';
import { fieldClass, labelClass, officeFocus, serverMessage, useMyGrants, useResource, type MyGrants } from '../../shells/OfficeShell';
import { TONE_CLASS, chipClass, stamp } from './goodsInModel';
import { useReturnFocus, type NcrPrefill } from './RaiseNcr';

export interface HoldConfig {
  enabled: boolean;
  qcHold: string;
  d365Link: string;
}

export type HoldStatus = 'REQUESTED' | 'CONFIRMED' | 'RELEASE_REQUESTED' | 'RELEASED';
export type HoldSourceKind = 'INSPECTION_FAILURE' | 'COFA' | 'FREE' | 'COMPLAINT';

export interface HoldView {
  holdId: number;
  /** Raw material or finished good (migration 054): what the person is told. */
  materialClass: 'RM' | 'FG';
  /** BATCH: the whole batch on QC Hold. PALLETS: finished-goods pallets scanned into 14-Q (migration 055). */
  scope: 'BATCH' | 'PALLETS';
  item: string;
  itemName: string | null;
  batch: string;
  source: { kind: HoldSourceKind; id: number | null };
  reason: string;
  ncr: { id: string; linkId: number | null };
  priorCode: string;
  requestedByUpn: string;
  requestedAt: string;
  ageMinutes: number;
  status: HoldStatus;
  drift: boolean;
  confirmedAt: string | null;
  release: { byUpn: string; at: string; restoreCode: string; ncrId: string; evidence: { kind: string; id: number }; note: string } | null;
  releasedAt: string | null;
  nudged: { hold: string | null; release: string | null };
  d365: { known: boolean; code: string | null; note: string | null } | null;
  todo: { set: string | null; words: string };
  d365Link: string;
}

export const STATUS_LABEL: Record<HoldStatus, string> = {
  REQUESTED: 'Asked - not yet set in D365',
  CONFIRMED: 'Held in D365',
  RELEASE_REQUESTED: 'Release asked - not yet set in D365',
  RELEASED: 'Released',
};

/** A code for a person; a hold state in words (the API's codeWords, domain/batchHold.ts). */
export const codeText = (c: string) =>
  c === 'HOLD:HELD'
    ? 'held (QC Hold on the batch)'
    : c === 'HOLD:FREE'
      ? 'not held'
      : c === 'HOLD:PART'
        ? 'stock in a quarantine warehouse, batch not on QC Hold'
        : c.trim() === ''
          ? 'blank'
          : c;

/** What D365 shows now, in words; never "not on hold" when it could not be read (rule 15). */
export function d365Text(h: Pick<HoldView, 'd365' | 'status'>): string {
  if (h.status === 'RELEASED') return 'Not read (released)';
  if (!h.d365) return 'Not known: not read this time';
  if (!h.d365.known) return h.d365.note ?? 'Not known';
  // A reading says the disposition, how much stock is held and how old INVENTORYV2's data is.
  return h.d365.note ? `${codeText(h.d365.code ?? '')}: ${h.d365.note}` : codeText(h.d365.code ?? '');
}

export function ageText(min: number): string {
  if (min < 90) return `${min} min`;
  if (min < 48 * 60) return `${Math.round(min / 60)} h`;
  return `${Math.round(min / 1440)} d`;
}

function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useHoldConfig(): HoldConfig | null {
  return useResource<HoldConfig>('/batch-holds/config').data;
}

const mutedSm = 'm-0 text-[length:var(--fs-sm)] text-[color:var(--muted)]';

/** The D365 instruction for one hold: what to set, and the Batches form. */
function ToDoLine({ h }: { h: HoldView }) {
  if (h.todo.set === null) return <p className={mutedSm}>{h.todo.words}</p>;
  return (
    <p className="m-0 text-[length:var(--fs-sm)] font-semibold text-[color:var(--warn)]">
      {h.todo.words}{' '}
      <a href={h.d365Link} target="_blank" rel="noreferrer" className={`underline ${officeFocus}`}>
        Open Batches in D365
      </a>
    </p>
  );
}

/* ── Put on hold ──────────────────────────────────────── */

export function PutOnHold({
  config,
  source,
  prefill,
  grants,
  onDone,
  startOpen = false,
  onClose,
}: {
  config: HoldConfig | null;
  source: { kind: HoldSourceKind; id: number | null };
  prefill: Pick<NcrPrefill, 'lines' | 'description'>;
  grants: MyGrants;
  onDone?: (h: HoldView) => void;
  /** Open the form at once: the caller's own button already asked (one press, not two). */
  startOpen?: boolean;
  /** Cancel hands back to the caller, which hides this, instead of showing a second button. */
  onClose?: () => void;
}) {
  const [open, setOpen] = useState(startOpen);
  const [made, setMade] = useState<HoldView | null>(null);
  const trigger = useReturnFocus<HTMLButtonElement>(open);
  if (config?.enabled !== true) return null;
  if (!(grants.known && (grants.holds('RELEASE') || grants.holds('RECEIVE')))) return null;
  if (made) {
    return (
      <div className="mt-[var(--s2)] flex flex-col gap-[var(--s1)]" aria-label="QC Hold asked">
        <Saved>
          Hold #{made.holdId} recorded for batch <span className="font-mono">{made.batch}</span>.
        </Saved>
        <p className="m-0 text-[length:var(--fs-sm)]">
          Before the hold D365 showed <span className="font-mono">{codeText(made.priorCode)}</span>; that is what a release puts
          back.
        </p>
        <ToDoLine h={made} />
        {onClose ? (
          <Button variant="outline" type="button" className="w-fit" onClick={onClose}>
            Done
          </Button>
        ) : null}
      </div>
    );
  }
  if (!open) {
    return (
      <Button ref={trigger} variant="outline" type="button" className="mt-[var(--s2)] w-fit" onClick={() => setOpen(true)}>
        Put batch on QC Hold
      </Button>
    );
  }
  return (
    <HoldForm
      source={source}
      prefill={prefill}
      onCancel={() => (onClose ? onClose() : setOpen(false))}
      onDone={(h) => {
        setMade(h);
        onDone?.(h);
      }}
    />
  );
}

function HoldForm({
  source,
  prefill,
  onDone,
  onCancel,
}: {
  source: { kind: HoldSourceKind; id: number | null };
  prefill: Pick<NcrPrefill, 'lines' | 'description'>;
  onDone: (h: HoldView) => void;
  onCancel: () => void;
}) {
  const [key] = useState(newKey);
  const [lineIx, setLineIx] = useState(0);
  const line = prefill.lines[lineIx] ?? null;
  const [item, setItem] = useState(line?.item ?? '');
  const [batch, setBatch] = useState(line?.batches[0] ?? '');
  const [reason, setReason] = useState(prefill.description);
  const [ncrId, setNcrId] = useState('');
  const [scope, setScope] = useState<'BATCH' | 'PALLETS'>('BATCH');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ hold: HoldView }>('/batch-holds', { clientKey: key, source, item, batch, reason, ncrId, scope });
      onDone(res.hold);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const ready = item.trim() !== '' && batch.trim() !== '' && reason.trim() !== '' && ncrId.trim() !== '';
  return (
    <div className="mt-[var(--s2)] flex max-w-[36rem] flex-col gap-[var(--s2)]" aria-label="Put batch on QC Hold">
      {prefill.lines.length > 1 ? (
        <label className="flex flex-col gap-[var(--s1)]">
          <span className={labelClass}>PO line</span>
          <select
            autoFocus
            className={`${fieldClass} ${officeFocus}`}
            value={lineIx}
            onChange={(e) => {
              const ix = Number(e.target.value);
              setLineIx(ix);
              setItem(prefill.lines[ix]?.item ?? '');
              setBatch(prefill.lines[ix]?.batches[0] ?? '');
            }}
          >
            {prefill.lines.map((l, i) => (
              <option key={`${l.item}-${i}`} value={i}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="flex flex-wrap gap-[var(--s2)]">
        <label className="flex flex-col gap-[var(--s1)]">
          <span className={labelClass}>Item</span>
          {/* First field takes focus when the form opens (finding 19), unless the PO line picker is first. */}
          <input autoFocus={prefill.lines.length <= 1} className={`${fieldClass} ${officeFocus} font-mono`} value={item} placeholder="100159" onChange={(e) => setItem(e.target.value)} />
        </label>
        <label className="flex flex-col gap-[var(--s1)]">
          <span className={labelClass}>Batch</span>
          <input className={`${fieldClass} ${officeFocus} font-mono`} value={batch} list="hold-batches" placeholder="F50418" onChange={(e) => setBatch(e.target.value)} />
          <datalist id="hold-batches">
            {(line?.batches ?? []).map((b) => (
              <option key={b} value={b} />
            ))}
          </datalist>
        </label>
        <label className="flex flex-col gap-[var(--s1)]">
          <span className={labelClass}>D365 NCR number</span>
          <input className={`${fieldClass} ${officeFocus} font-mono`} value={ncrId} placeholder="MAU-003701" onChange={(e) => setNcrId(e.target.value)} />
        </label>
      </div>
      <fieldset className="flex flex-col gap-[var(--s1)]">
        <legend className={labelClass}>What is held</legend>
        <label className="flex items-center gap-[var(--s2)]">
          <input type="radio" name="hold-scope" checked={scope === 'BATCH'} onChange={() => setScope('BATCH')} />
          The whole batch (QC Hold; raw material also moves to 05-Q / 05A-Q)
        </label>
        <label className="flex items-center gap-[var(--s2)]">
          <input type="radio" name="hold-scope" checked={scope === 'PALLETS'} onChange={() => setScope('PALLETS')} />
          Some pallets of a finished good (scanned into 14-Q)
        </label>
      </fieldset>
      <label className="flex flex-col gap-[var(--s1)]">
        <span className={labelClass}>Reason</span>
        <textarea className={`${fieldClass} ${officeFocus}`} value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <p className={mutedSm}>
        The app reads the batch's disposition code from D365 now and keeps it, then tells you what to set. It does not change D365 itself.
      </p>
      {error ? (
        <p role="alert" className="m-0 text-[length:var(--fs-sm)] font-semibold text-[color:var(--warn)]">
          {error}
        </p>
      ) : null}
      <div className="flex gap-[var(--s2)]">
        <Button type="button" disabled={busy || !ready} onClick={() => void submit()}>
          {busy ? 'Reading D365…' : 'Record the hold'}
        </Button>
        <Button variant="outline" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* ── Release ──────────────────────────────────────────── */

const EVIDENCE: Array<[string, string]> = [
  ['COFA_DECISION', 'Certificate decision #'],
  ['INSPECTION', 'Incoming inspection #'],
  ['FAILURE_CLOSURE', 'Failure close-out #'],
];

function ReleaseForm({ h, onDone, onCancel }: { h: HoldView; onDone: () => void; onCancel: () => void }) {
  const [key, setKey] = useState(newKey);
  const [kind, setKind] = useState('COFA_DECISION');
  const [evId, setEvId] = useState('');
  const [ncrId, setNcrId] = useState(h.ncr.id);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/batch-holds/${h.holdId}/release`, { clientKey: key, ncrId: ncrId.trim() || undefined, evidence: { kind, id: Number(evId) }, note });
      onDone();
    } catch (err) {
      setError(serverMessage(err));
      // Refused at the door or on the request: nothing was stored, so the next press is a new one.
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) setKey(newKey());
    } finally {
      setBusy(false);
    }
  }

  const ready = Number.isInteger(Number(evId)) && Number(evId) > 0 && note.trim() !== '';
  return (
    <div className="flex max-w-[36rem] flex-col gap-[var(--s2)]" aria-label={`Release hold #${h.holdId}`}>
      <p className={mutedSm}>
        {h.scope === 'PALLETS' ? 'Release moves the pallets back out of 14-Q; the batch code stays' : 'Release puts back the code D365 showed before the hold:'}{' '}
        <span className="font-mono">{codeText(h.priorCode)}</span>.
      </p>
      <div className="flex flex-wrap gap-[var(--s2)]">
        <label className="flex flex-col gap-[var(--s1)]">
          <span className={labelClass}>Rests on</span>
          <select autoFocus className={`${fieldClass} ${officeFocus}`} value={kind} onChange={(e) => setKind(e.target.value)}>
            {EVIDENCE.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-[var(--s1)]">
          <span className={labelClass}>Its number</span>
          <input className={`${fieldClass} ${officeFocus} w-[8rem] tabular-nums`} inputMode="numeric" value={evId} onChange={(e) => setEvId(e.target.value)} />
        </label>
        <label className="flex flex-col gap-[var(--s1)]">
          <span className={labelClass}>D365 NCR number</span>
          <input className={`${fieldClass} ${officeFocus} font-mono`} value={ncrId} onChange={(e) => setNcrId(e.target.value)} />
        </label>
      </div>
      <label className="flex flex-col gap-[var(--s1)]">
        <span className={labelClass}>Why it can be released</span>
        <textarea className={`${fieldClass} ${officeFocus}`} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error ? (
        <p role="alert" className="m-0 text-[length:var(--fs-sm)] font-semibold text-[color:var(--warn)]">
          {error}
        </p>
      ) : null}
      <div className="flex gap-[var(--s2)]">
        <Button type="button" disabled={busy || !ready} onClick={() => void submit()}>
          {busy ? 'Recording…' : 'Record the release'}
        </Button>
        <Button variant="outline" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* ── The Holds panel on Goods in ──────────────────────── */

interface HoldsPayload {
  holds: HoldView[];
}

/** Tells the tab its count: a number once read, null when it could not be read (rule 15: never 0 for unknown). */
export function HoldsPanel({ onCount }: { onCount?: (n: number | null) => void } = {}) {
  // (UI/UX audit, 31 Oct 2026, UX-10, rule 15) The tab said "QC Holds are switched off" while the
  // switch was still loading and when it could not be read: a guess dressed as a fact. Three
  // different answers now; only a read that says enabled === false gets the off sentence, in
  // plain words (the setting's name is for us, not QA). The buttons elsewhere still hide while
  // the switch is unknown (useHoldConfig) - offering an action is not making a claim.
  const res = useResource<HoldConfig>('/batch-holds/config');
  if (res.error) {
    return (
      <section aria-label="QC Holds" className="flex flex-wrap items-center gap-[var(--s2)]">
        <p role="alert" className="m-0 font-semibold text-[color:var(--warn)]">
          Could not read whether QC Holds are in use: {res.error}
        </p>
        <Button variant="outline" type="button" onClick={res.reload}>
          Try again
        </Button>
      </section>
    );
  }
  if (!res.data) return <p className={mutedSm} role="status">Loading…</p>;
  if (res.data.enabled !== true) {
    return <p className={mutedSm}>QC Holds are not in use: the app does not ask for or follow QC Holds in D365.</p>;
  }
  return <Holds config={res.data} onCount={onCount} />;
}

function Holds({ config, onCount }: { config: HoldConfig; onCount?: (n: number | null) => void }) {
  const [params] = useSearchParams();
  const focus = Number(params.get('hold')) || null;
  const res = useResource<HoldsPayload>(focus ? `/batch-holds?hold=${focus}` : '/batch-holds');
  const grants = useMyGrants();
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [releasing, setReleasing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  // What the last Check now or release recorded (audit finding 15), until the next one.
  const [saved, setSaved] = useState<string | null>(null);
  const mayAct = grants.known && (grants.holds('RELEASE') || grants.holds('RECEIVE'));
  const mayRelease = grants.known && grants.holds('RELEASE');

  // Finding 19: the Release form replaces its button, so when it closes the button is looked up by
  // id and given the focus back.
  const returnTo = useRef<string | null>(null);
  useEffect(() => {
    if (releasing === null && returnTo.current) {
      document.getElementById(returnTo.current)?.focus();
      returnTo.current = null;
    }
  }, [releasing]);

  async function checkNow() {
    setChecking(true);
    setCheckError(null);
    setSaved(null);
    try {
      await api.post('/batch-holds/check', focus ? { holdIds: [focus] } : {});
      setSaved('D365 read just now: "D365 now" on each hold is what it showed.');
      res.reload();
    } catch (err) {
      setCheckError(serverMessage(err));
    } finally {
      setChecking(false);
    }
  }

  const holds = res.data?.holds ?? [];
  // The tab's count: the open holds listed. A focused ?hold= view is one hold, not the count.
  useEffect(() => onCount?.(res.error ? null : res.data && !focus ? holds.length : null), [res.error, res.data, focus, holds.length, onCount]);
  return (
    <section id="holds" aria-label="QC Holds" className="flex flex-col gap-[var(--s2)]">
      <div className="flex flex-wrap items-center gap-[var(--s2)]">
        <h2 className="m-0 text-[length:var(--fs-md)] font-semibold">QC Holds{focus ? ` · hold #${focus}` : ''}</h2>
        {mayAct ? (
          <Button variant="outline" type="button" disabled={checking} onClick={() => void checkNow()}>
            {checking ? 'Reading D365…' : 'Check now'}
          </Button>
        ) : null}
        {mayAct && !adding ? (
          <Button variant="outline" type="button" onClick={() => setAdding(true)}>
            Put on hold
          </Button>
        ) : null}
        {focus ? (
          <a href="/office/goods-in#holds" className={`text-[length:var(--fs-sm)] underline ${officeFocus}`}>
            All open holds
          </a>
        ) : null}
        <a href="/office/holds" className={`text-[length:var(--fs-sm)] underline ${officeFocus}`}>
          Holds register: age, waiting on, value held
        </a>
      </div>
      <p className={mutedSm}>
        The app does not change D365: a person sets the hold in D365 (QC Hold on the batch, or pallets scanned into 14-Q), and the app reads it back every 15 minutes or on Check now.
      </p>
      {adding ? (
        <PutOnHold
          config={config}
          source={{ kind: 'FREE', id: null }}
          prefill={{ lines: [], description: '' }}
          grants={grants}
          onDone={() => res.reload()}
          startOpen
          onClose={() => setAdding(false)}
        />
      ) : null}
      {checkError ? (
        <p role="alert" className="m-0 text-[length:var(--fs-sm)] font-semibold text-[color:var(--warn)]">
          {checkError}
        </p>
      ) : null}
      {saved ? <Saved>{saved}</Saved> : null}
      {res.error ? (
        <p role="alert" className="m-0 font-semibold text-[color:var(--warn)]">
          The holds could not be read: {res.error} Not known is not "no holds".{' '}
          <Button variant="outline" type="button" onClick={res.reload}>
            Try again
          </Button>
        </p>
      ) : res.data && holds.length === 0 ? (
        <p className={mutedSm}>{focus ? `No hold #${focus}.` : 'No open holds recorded in the app.'}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-[var(--s3)] p-0">
          {holds.map((h) => (
            <li key={h.holdId} className="flex flex-col gap-[var(--s1)] border-l-4 border-[color:var(--border)] pl-[var(--s2)]">
              <div className="flex flex-wrap items-center gap-[var(--s2)]">
                <span className="font-semibold">
                  #{h.holdId} · <span className="font-mono">{h.item}</span>
                  {h.itemName ? ` ${h.itemName}` : ''} · batch <span className="font-mono">{h.batch}</span>
                </span>
                <span className={`${chipClass} ${TONE_CLASS[h.drift ? 'bad' : h.status === 'CONFIRMED' || h.status === 'RELEASED' ? 'quiet' : 'warn']}`}>
                  {h.drift ? 'Removed in D365 outside the app' : STATUS_LABEL[h.status]}
                </span>
              </div>
              <p className="m-0 text-[length:var(--fs-sm)]">
                D365 now: <span className={`font-mono ${h.d365 && !h.d365.known ? 'text-[color:var(--warn)]' : ''}`}>{d365Text(h)}</span> · before the hold:{' '}
                <span className="font-mono">{codeText(h.priorCode)}</span> · age {ageText(h.ageMinutes)} · NCR <span className="font-mono">{h.ncr.id}</span> · asked by{' '}
                {h.requestedByUpn} {stamp(h.requestedAt)}
              </p>
              <p className={mutedSm}>Reason: {h.reason}</p>
              {h.release ? (
                <p className={mutedSm}>
                  Release asked by {h.release.byUpn} {stamp(h.release.at)}: {h.release.note} (rests on {h.release.evidence.kind.toLowerCase().replace('_', ' ')} #
                  {h.release.evidence.id})
                </p>
              ) : null}
              {h.nudged.hold || h.nudged.release ? <p className={mutedSm}>A "not yet set in D365" card was sent {stamp(h.nudged.release ?? h.nudged.hold)}.</p> : null}
              <ToDoLine h={h} />
              {mayRelease && (h.status === 'REQUESTED' || h.status === 'CONFIRMED') ? (
                releasing === h.holdId ? (
                  <ReleaseForm
                    h={h}
                    onCancel={() => setReleasing(null)}
                    onDone={() => {
                      setSaved(
                        h.scope === 'PALLETS'
                          ? `Release of hold #${h.holdId} recorded: transfer the pallets from 14-Q back to WH14 in D365.`
                          : `Release of hold #${h.holdId} recorded: set Disposition code back to ${codeText(h.priorCode)} in D365.`,
                      );
                      setReleasing(null);
                      res.reload();
                    }}
                  />
                ) : (
                  <Button
                    id={`release-hold-${h.holdId}`}
                    variant="outline"
                    type="button"
                    className="w-fit"
                    onClick={() => {
                      returnTo.current = `release-hold-${h.holdId}`;
                      setSaved(null);
                      setReleasing(h.holdId);
                    }}
                  >
                    Release
                  </Button>
                )
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
